import { Component, OnDestroy, OnInit } from '@angular/core';
import { CharterDAO } from '../../../../../../core/services/dao/charterDAO';
import { ActivatedRoute, Router } from '@angular/router';
import { Charter } from 'anchor-shared/lib/model/entity/charter';
import { CaptainDAO } from '../../../../../../core/services/dao/captainDAO';
import { Captain } from 'anchor-shared/lib/model/entity/captain';
import { AppMessagingService } from '../../../../../../core/services/app-messaging.service';
import { ContainsSubscriptions } from '../../../../../../core/services/interfaces/contains-subscriptions';
import { Subscription } from 'rxjs/Rx';
import { unsubscribeFromAll } from '../../../../../../core/pure-functions/rx-helper';
import * as moment from 'moment';
import { CaptainGig } from 'anchor-shared/lib/model/entity/captainGig';
import { CaptainGigDAO } from '../../../../../../core/services/dao/captainGigDAO';
import { ChickenTestDialogService } from '../../../../../shared/dialogs/chicken-test-dialog/chicken-test-dialog.service';
import { CharterDateTimeFormat } from '../charter-captains.component';
import { getFadeInTransition } from '../../../../../../core/pure-functions/animations/ng-animations-provider';
import { EnvironmentalMediaService } from '../../../../../../core/services/environmental-media.service';
import { CharterDateSingleDigitTimeFormat } from '../../../../../../core/models/interface/time-and-date/time-and-date-formatting-constants';

@Component({
  selector: 'app-assign-captains',
  templateUrl: './assign-captains.component.html',
  styleUrls: ['./assign-captains.component.scss'],
  animations: [getFadeInTransition(1000)],
})
export class AssignCaptainsComponent
  implements OnInit, OnDestroy, ContainsSubscriptions
{
  charter: Charter;
  captainGigs: CaptainGig[];
  sortBy: 'Response' | 'Name' = 'Response';
  subs: Subscription[] = [];
  formattedCharterDate: string;
  abbreviatedCharterDate: string;
  captainsWithoutGigs: Captain[] = [];
  captainAssigned = false;

  constructor(
    private charterDao: CharterDAO,
    private captainGigDao: CaptainGigDAO,
    private router: Router,
    private route: ActivatedRoute,
    private appMessagingService: AppMessagingService,
    private chickenTestDialog: ChickenTestDialogService,
    private environmentalMediaService: EnvironmentalMediaService,
  ) {}

  ngOnInit() {
    this.initCharterGigView();
  }

  ngOnDestroy() {
    unsubscribeFromAll(this.subs);
  }

  initCharterGigView(): void {
    let charterId = this.route.snapshot.params.charterId;

    const charterSub = this.charterDao
      .getCharterDetailsWithCaptains(charterId)
      .subscribe((charter) => {
        this.charter = charter;
        if (charter.captainGigs) {
          this.formattedCharterDate = moment(
            charter.date + ' ' + charter.startTime,
            CharterDateTimeFormat,
          ).format('MMMM D, YYYY - h:mm A');
          this.abbreviatedCharterDate = moment(
            charter.date + ' ' + charter.startTime,
            CharterDateTimeFormat,
          ).format('MMMM Do');

          this.captainGigs = this.charter.captainGigs;

          this.checkForCaptainsWithoutGigs();

          this.sortCaptains();
        }
      });
    this.subs.push(charterSub);
  }

  checkForCaptainsWithoutGigs() {
    this.captainsWithoutGigs = [];
    this.charter.boat.vesselCaptainMap.forEach((vesselCaptain) => {
      let captainWithGig = this.captainGigs.find((gig) => {
        return gig.captainId === vesselCaptain.captainId;
      });
      if (!captainWithGig) {
        this.captainsWithoutGigs.push(vesselCaptain.captain);
      }
    });
  }

  assignCaptain(captainGig: CaptainGig) {
    let assignTitle = 'Confirm Assignment';
    let captainFullName = `${captainGig.captain.user.firstName} ${captainGig.captain.user.lastName}`;
    let assignQuestion = `Assign ${captainFullName} to charter #${this.charter.id}?`;
    let affirmativeText = `Assign Captain ${captainGig.captain.user.lastName}`;
    let negativeText = 'Cancel';

    const chickenSub = this.chickenTestDialog
      .open(
        assignQuestion,
        affirmativeText,
        negativeText,
        null,
        null,
        assignTitle,
      )
      .subscribe((userAccepted) => {
        if (userAccepted) {
          const assignSub = this.charterDao
            .headCaptainAssignCaptainToCharter(
              this.charter.id,
              captainGig.captain,
            )
            .subscribe((success) => {
              if (success) {
                this.captainAssigned = true;
              } else {
                this.appMessagingService.openSnack(
                  'There was an error assigning the captain. Please try again.',
                );
              }
            });
          this.subs.push(assignSub);
        }
      });
    this.subs.push(chickenSub);
  }

  sortCaptains() {
    if (this.sortBy === 'Response') {
      let avail = [];
      let unavail = [];
      let noResponse = [];

      this.captainGigs.forEach((gig: CaptainGig) => {
        if (gig.responseText === 'Available') {
          avail.push(gig);
        } else if (gig.responseText === 'Unavailable') {
          unavail.push(gig);
        } else if (gig.responseText === 'No Response') {
          noResponse.push(gig);
        }

        this.findAssignedCharters(gig);
      });

      this.captainGigs = [...avail, ...unavail, ...noResponse];
    } else if (this.sortBy === 'Name') {
      this.captainGigs.sort((a, b) => {
        if (
          a.captain.user.firstName.toLowerCase() <
          b.captain.user.firstName.toLowerCase()
        ) {
          return -1;
        }
        if (
          a.captain.user.firstName.toLowerCase() >
          b.captain.user.firstName.toLowerCase()
        ) {
          return 1;
        }
        return 0;
      });
    }
  }

  goBack() {
    this.router.navigate(['manage/charter-captains']);
  }

  createGigForCaptain(captainId: number) {
    const createGigSub = this.captainGigDao
      .adminCreateCaptainGig(captainId, this.charter.id)
      .subscribe((newGig) => {
        this.initCharterGigView();
      });
    this.subs.push(createGigSub);
  }

  private findAssignedCharters(gig) {
    let charterDate = moment(this.charter.date).format('YYYY-MM-DD').toString();

    const assignedCaptSub = this.charterDao
      .getChartersByCaptainAndDay(gig.captainId, charterDate)
      .subscribe((chartersOnDate) => {
        gig.captain.assignedCharters = [];
        if (chartersOnDate) {
          chartersOnDate.forEach((charter) => {
            gig.captain.assignedCharters.push(charter);
          });
        }
      });
    this.subs.push(assignedCaptSub);
  }

  getManageCharterUrl(charterId: number) {
    let url =
      this.environmentalMediaService.getClientBaseURL() +
      '/manage/manage-charter/' +
      charterId;
    return url;
  }

  isCharterConflicted(assignedCharter: Charter) {
    let charterConflicted = true;
    let assignedCharterStart = moment(
      `${assignedCharter.date} ${assignedCharter.startTime}`,
      CharterDateSingleDigitTimeFormat,
    );
    let assignedCharterEnd = assignedCharterStart
      .clone()
      .add(assignedCharter.duration, 'hour');
    let requestCharterStart = moment(
      `${this.charter.date} ${this.charter.startTime}`,
      CharterDateSingleDigitTimeFormat,
    );
    let requestCharterEnd = requestCharterStart
      .clone()
      .add(this.charter.duration, 'hour');

    if (
      assignedCharterStart.isBefore(requestCharterEnd) &&
      requestCharterStart.isBefore(assignedCharterEnd)
    ) {
      charterConflicted = true;
    } else {
      charterConflicted = false;
    }
    return charterConflicted;
  }
}
